import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { GoogleAuthButton } from "@/components/GoogleAuthButton";
import { Turnstile, TURNSTILE_ENABLED } from "@/components/Turnstile";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

export default function Login() {
  const { login } = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get("redirect") || "/account";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [resetSignal, setResetSignal] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (TURNSTILE_ENABLED && !token) {
      setError("Please complete the human verification.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password, token);
      toast.success("Welcome back!");
      navigate(redirectTo);
    } catch (err) {
      setError(err.message);
      setResetSignal((n) => n + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-5 py-16 min-h-[70vh]" data-testid="login-page">
      <Seo title="Sign In" path="/login" noindex />
      <Reveal className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Welcome Back</p>
        <h1 className="font-serif text-4xl mt-3 text-charcoal">Sign In</h1>
        <div className="w-16 h-px bg-gold mx-auto mt-5" />
      </Reveal>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-charcoal mb-1.5" htmlFor="login-email">Email address<span className="text-pink">*</span></label>
          <input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="w-full border border-gray-300 rounded px-4 py-3.5 text-base text-charcoal focus:outline-none focus:border-charcoal focus:ring-1 focus:ring-charcoal transition-colors" />
        </div>
        <div>
          <label className="block text-sm font-semibold text-charcoal mb-1.5" htmlFor="login-password">Password<span className="text-pink">*</span></label>
          <div className="relative flex items-stretch">
            <input id="login-password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full border border-gray-300 rounded rounded-r-none border-r-0 px-4 py-3.5 text-base text-charcoal focus:outline-none focus:border-charcoal focus:ring-1 focus:ring-charcoal transition-colors" />
            <button type="button" onClick={() => setShowPassword((s) => !s)} data-testid="login-show-password"
              className="shrink-0 px-4 border border-gray-300 rounded rounded-l-none text-sm font-semibold text-pink hover:bg-pink/5 transition-colors">
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-charcoal cursor-pointer select-none">
            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="login-remember" className="w-4 h-4 accent-pink" />
            Remember me
          </label>
          <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-charcoal underline hover:text-pink transition-colors">Forgotten your password?</Link>
        </div>

        {error && <p className="text-sm text-pink" data-testid="login-error">{error}</p>}
        <Turnstile onVerify={setToken} action="login" resetSignal={resetSignal} />
        <button type="submit" disabled={loading} data-testid="login-submit"
          className="w-full bg-pink text-white py-4 rounded text-base font-semibold hover:bg-pink-hover transition-colors disabled:opacity-60">
          {loading ? "Logging in..." : "Log in"}
        </button>
      </form>

      <GoogleAuthButton redirectTo={redirectTo} />

      <p className="text-center text-sm text-gray-500 mt-6">
        New to Zebaa? <Link to="/register" data-testid="go-register" className="text-pink hover:underline">Create an account</Link>
      </p>
    </div>
  );
}
